import SettingsMenu from "views/admin/main/account/component/SettingsMenu";
import SaveStatus from "components/actions/SaveStatus"; 
import React, { useEffect, useState } from 'react';
import { Check, Package, Calendar, MousePointer, Truck } from 'lucide-react';
import TagList from "components/tagProduct/tagList";
import ZoneList from "components/zone/zoneList";
import VatAccountingCodeList from "components/vatAccountingCode/vatAccountingCodeList";
import { getMyInfo, updateMyInfo } from "interfaces/user";
import { toast } from "react-toastify";

const AdvancedSettings = () => {
  const [selectedOption, setSelectedOption] = useState<string[]>([]);
  const [isAutoSave] = useState(true);
  const [stockRemovalReasons, setStockRemovalReasons] = useState([]);
  const [saveStatus, setSaveStatus] = useState<any>("none");
  const [initialized, setInitialized] = useState(false);
  const [isParent, setIsParent] = useState(true);
  const [firstUpdate, setFirstUpdate] = useState(false);

  const sameIds = (a?: string[], b?: string[]) =>
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((x) => b.includes(x));

 // Get my info -> setStockRemovalReasons  
  const initUser = async () => {  
    try {
      const res = await getMyInfo();  
      if (res?.data) {
        const fromDb = Array.isArray(res.data.stockRemovalReasons) ? res.data.stockRemovalReasons : [];
        setStockRemovalReasons(fromDb);
        setSelectedOption(fromDb); 
        // User is parent if they don't have an ownerId
        setIsParent(!res.data.ownerId);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setInitialized(true);
    }
  };

useEffect(() => {
  initUser();
}, []);

useEffect(() => {
  if (!initialized || !isAutoSave || !firstUpdate) return;

  setSaveStatus('saving');
  const delayDebounceFn = setTimeout(async () => {
    try {
      const { data, error } = await updateMyInfo({ stockRemovalReasons });
      if (error || !data) {
        setSaveStatus('error');
        return;
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('none'), 1500);
    } catch (err) {
      console.error(err);
      setSaveStatus('error');
    }
  }, 600);
  return () => clearTimeout(delayDebounceFn);
}, [initialized, isAutoSave, stockRemovalReasons]);

 useEffect(() => {
  console.log('Raisons de suppression du stock mises à jour :', stockRemovalReasons);
}, [stockRemovalReasons]);


const handleStockReason = (id: string[]) => {

  if (!isParent) {
    setSaveStatus('error');
    toast.error("Seul le compte principal peut modifier cette configuration.");
    return;
  }

  if (!firstUpdate) setFirstUpdate(true);
  setSelectedOption(id);
  setStockRemovalReasons(id); 
};

  
  const deductionOptions = [
    {
      id: ['recurrent-order', 'command-page-order', 'manual-order'],
      title: 'À la commande',
      description: 'Le stock est défalqué automatiquement lors de la création d\'une commande récurrente, via la page de commande ou manuellement par l\'administrateur.',
      icon: <Calendar className="w-6 h-6" />,
      timing: 'Automatique - Création de commande'
    },
    {
      id: ['prepared-production'],
      title: 'À la préparation d\'une production',
      description: 'Le stock est défalqué lorsque la production est terminée depuis la page de production.',
      icon: <MousePointer className="w-6 h-6" />,
      timing: 'Automatique - Validation d\'une production'
    },
    {
      id: ['prepared-order'],
      title: 'À la préparation d\'une commande',
      description: 'Le stock est défalqué lorsque la commande est marquée comme préparée depuis la page de gestion des commandes.',
      icon: <Package className="w-6 h-6" />,
      timing: 'Automatique - Commande préparée'
    },
    {
      id: ['delivered-order'],
      title: 'À la livraison',
      description: 'Le stock est défalqué seulement quand la livraison est confirmée',
      icon: <Truck className="w-6 h-6" />,
      timing: 'Automatique - Confirmation de livraison'
    }
  ];

  

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex justify-between">
                <SettingsMenu path="/settings/advanced" />
                <SaveStatus status={saveStatus} />
            </div>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-gray-900 font-semibold text-2xl mb-2">Configuration du stock</h1>
              <p className="text-gray-600 text-sm">Définissez le moment de défalcation de votre stock</p>
            </div>
            <div className="flex items-center gap-2">
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white border-gray-100 rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-toola-500 bg-opacity-10 p-3 rounded-lg">
              <Package className="w-6 h-6 text-toola-500" />
            </div>
            <div>
              <h2 className="text-gray-900 font-semibold text-lg mb-2">Moment de défalcation du stock</h2>
              <p className="text-gray-600 text-sm">
                Choisissez à quel moment le stock doit être automatiquement réduit lors du processus de commande.
                Cette configuration affectera tous les produits de votre inventaire.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {deductionOptions.map((option) => (
              <div
                key={option.id.join("-")}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:border-toola-500 hover:shadow-md ${
                  sameIds(selectedOption, option.id)
                    ? 'border-toola-500 bg-toola-500 bg-opacity-5'
                    : 'border-gray-200'
                }`}
                onClick={() => handleStockReason(option.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-2 rounded-lg ${
                      sameIds(selectedOption, option.id) 
                        ? 'bg-toola-500 text-white' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {option.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-gray-900 font-semibold">{option.title}</h3>
                        {sameIds(selectedOption, option.id) && (
                          <Check className="w-5 h-5 text-toola-500" />
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{option.description}</p>
                      <div className="inline-flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <span className="text-gray-600 text-xs">{option.timing}</span>
                      </div>
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="deduction"
                    value={option.id}
                    checked={sameIds(selectedOption, option.id)}
                    onChange={(e) => handleStockReason(option.id)}
                    className="w-4 h-4 text-toola-500 focus:ring-toola-500 focus:ring-2"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <TagList />
        </div>

        <div className="mt-8">
          <ZoneList />
        </div>

        <div className="mt-8">
          <VatAccountingCodeList />
        </div>
      </div>
    </div>
  );
};



export default AdvancedSettings;
